/**
 * An email as it will arrive (1.4.1): the heading (the wording's `# ` line), the words — the `> ` lines as
 * the details box, `- ` lines as a list — the buttons the app adds, and the footer under the signature
 * (the Director's wording; the internal memos carry the address line only). Nothing here sends anything and
 * the buttons do nothing.
 */
import { useTranslation } from 'react-i18next';
import { emailFooter, isInternalMessage, renderMessage, type Lang, type TokenValues } from '@shared/email';
import { splitHeading } from '@shared/wording';

export { templateButtons, sampleTokens } from '@shared/email-samples';

function Block({ text }: { text: string }) {
  const lines = text.split('\n');
  if (lines.every((l) => /^>\s?/.test(l))) {
    return (
      <div className="email-details">
        {lines.map((l, i) => (
          <div key={i}>{l.replace(/^>\s?/, '')}</div>
        ))}
      </div>
    );
  }
  const plain = lines.filter((l) => !/^- /.test(l));
  const items = lines.filter((l) => /^- /.test(l)).map((l) => l.slice(2));
  return (
    <>
      {plain.length ? <p style={{ whiteSpace: 'pre-line', margin: 0 }}>{plain.join('\n')}</p> : null}
      {items.length ? (
        <ul style={{ margin: 0, paddingLeft: 22 }}>
          {items.map((x, i) => (
            <li key={i}>{x}</li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

export function EmailPreview({
  code,
  subject,
  body,
  lang,
  buttons,
  office,
  tokens,
  testId,
}: {
  code: string;
  subject: string;
  body: string;
  lang: Lang;
  buttons: string[];
  office: { phone: string | null; email: string | null };
  tokens: TokenValues;
  testId?: string;
}) {
  const { t } = useTranslation();
  const r = renderMessage({ subject, body }, tokens);
  const { heading, body: words } = splitHeading(r.body.trim());
  // 1.8.3: the footer speaks of buttons only when this email has them
  const footer = emailFooter(lang, office, isInternalMessage(code), buttons.length > 0);
  return (
    <section className="email" aria-label={t('emailSettings.theEmail')} lang={lang} data-testid={testId}>
      <div className="email-meta">
        <div>
          <span className="lbl">{t('email.from')}</span> AB Scheduling
        </div>
        <div>
          <span className="lbl">{t('email.subject')}</span> {r.subject}
        </div>
      </div>
      <div className="email-body">
        {heading ? <p className="email-heading">{heading}</p> : null}
        {words
          .split(/\n{2,}/)
          .filter((p) => p.trim())
          .map((p, i) => (
            <Block key={i} text={p} />
          ))}
        {buttons.map((b, i) => (
          <span key={b} className={`big-btn${i ? ' secondary' : ''}`} aria-disabled="true">
            {b}
          </span>
        ))}
        <div className="email-footer">
          <p>{footer.address}</p>
          {footer.notice ? (
            <p>
              {footer.notice[0]}
              <strong>{footer.notice[1]}</strong>
              {footer.notice[2]}
            </p>
          ) : null}
        </div>
        {r.unknown.length ? (
          <p className="warn" role="note">
            {t('email.unknownInWording', { tokens: r.unknown.map((u) => `[${u}]`).join(', ') })}
          </p>
        ) : null}
      </div>
    </section>
  );
}
